// Scrolls an element into view. Shared by scrollToId and the pager, so both
// pick the same reduced-motion behaviour.
//
// A landing page whose panels reveal themselves as you scroll THROUGH them has a
// catch for jump navigation: a section's top edge — exactly where scrollIntoView
// lands — is where its scrub sits at zero, so the panel you asked for is still
// blank and you have to keep scrolling to make it appear. Sections that start out
// blank say so with `data-jump="settled"`, and a jump lands at the far end of their
// scroll range instead, with the panel fully drawn. Nothing about scrolling in
// normally changes: the reveal still scrubs across the whole section.
export function scrollElementIntoView(el, { instant = false } = {}) {
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const behavior = reduce || instant ? "auto" : "smooth";
  const settled = settledScrollTop(el);

  if (settled === null) el.scrollIntoView({ behavior, block: "start" });
  else window.scrollTo({ top: settled, behavior });
}

// Where a jump to `el` comes to rest, as a document offset — the same position
// scrollElementIntoView above scrolls to, worked out rather than performed.
//
// The landing page's keyboard paging needs to know where every page IS before it can
// say which one comes next from where the reader is standing, and its answer has to
// agree with the pager's to the pixel: a stop computed even slightly below where a jump
// actually lands would make the next keypress aim at the page the reader is already on.
export function scrollTargetTop(el) {
  const settled = settledScrollTop(el);
  if (settled !== null) return settled;

  // What scrollIntoView({ block: "start" }) does: park the element's top edge below the
  // scroller's own scroll-padding (html.snap-pages sets it to the sticky header) plus
  // whatever scroll-margin the element asks for on top of that.
  const padding = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
  const margin = parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
  return Math.round(window.scrollY + el.getBoundingClientRect().top - padding - margin);
}

// Where `el`'s scroll-driven reveal has finished, or null if it has none to wait
// for — either it didn't opt in, or it's no taller than the viewport and so has no
// scroll range of its own (which is the shape every one of these takes under
// reduced motion, where the content is simply there from the start).
function settledScrollTop(el) {
  if (el.dataset.jump !== "settled") return null;

  const box = el.getBoundingClientRect();
  const range = box.height - window.innerHeight;
  return range > 0 ? Math.round(window.scrollY + box.top + range) : null;
}

// Smooth in-page scrolling for the landing's hash links. Scoped to where it's
// wired up (rather than a global `html { scroll-behavior: smooth }`) so it never
// changes how route navigation scrolls elsewhere in the app. Honours
// prefers-reduced-motion; scroll-padding-top on html.snap-pages clears the
// sticky header.
export function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  scrollElementIntoView(el);
}

// Click handler for a link to an in-page section — smooth-scrolls instead of jumping
// and reflects the section in the URL without triggering a route change.
//
// Accepts both "#vragen" and "/#vragen". The second form is what the footer uses,
// because the footer is shared with the public content pages, where those sections
// don't exist: there the element is missing, this handler bows out, and vue-router
// navigates to the landing page with the hash — which resolveHash() below then honours
// on arrival. Same markup, right behaviour on both.
export function anchorScroll(event) {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button) return;

  const href = event.currentTarget.getAttribute("href") ?? "";
  const index = href.indexOf("#");
  if (index === -1) return;

  const hash = href.slice(index);
  const el = document.getElementById(decodeURIComponent(hash.slice(1)));
  if (!el) return;

  event.preventDefault();
  scrollElementIntoView(el);
  history.replaceState(history.state, "", hash);
}

// Honour the URL's hash once the page it belongs to has actually mounted.
//
// A direct load of /#hoe-het-werkt used to leave the visitor somewhere else entirely:
// the browser resolves the hash while #app is still the pre-rendered shell, the real
// section arrives later at a different offset, and nothing looks at the hash again —
// so the URL said one section and the screen showed another. Waiting for the webfonts
// (they change every section's height) plus two frames of layout, then scrolling
// explicitly, is what makes a shared link land where it claims to. Instant, not
// smooth: a fresh load easing down through six sections looks like a bug, not a scroll.
export async function resolveHash() {
  const hash = window.location.hash;
  if (hash.length < 2) return;

  await document.fonts?.ready?.catch?.(() => {});
  await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

  const el = document.getElementById(decodeURIComponent(hash.slice(1)));
  if (el) scrollElementIntoView(el, { instant: true });
}

// Click handler for the home/brand link: smooth-scroll to the top and drop any
// "#section" fragment so the URL reads cleanly again. Modified clicks (new tab,
// middle-click) are left to the browser.
export function scrollToTop(event) {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button) return;
  event.preventDefault();
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
  history.replaceState(history.state, "", window.location.pathname + window.location.search);
}
